import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as THREE from "three";
import { select as d3Select } from "d3-selection";
import "d3-transition";
import {
  zoom as d3Zoom,
  zoomIdentity,
  ZoomTransform,
} from "d3-zoom";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  Simulation,
  SimulationNodeDatum,
} from "d3-force";
import { Spin } from "antd";
import "./controls-panel/ControlsPanel.scss";
import Tooltip from "./tooltip/Tooltip";

const COLOR_HIGHLIGHT: [number, number, number] = [0.353, 0.341, 1.0]; // #1b1a4c
const COLOR_RELATED: [number, number, number] = [0.333, 0.329, 0.698]; // hsl(237 48% 24%)
const COLOR_GENRE: [number, number, number] = [0.541, 0.906, 0.604]; // #8AE79A
const COLOR_NORMAL: [number, number, number] = [0.937, 0.937, 0.937]; // #EFEFEF
const COLOR_STROKE: [number, number, number] = [0.231, 0.227, 0.608]; // hsl(238, 43%, 22%)
const COLOR_LINK_HIGHLIGHT: [number, number, number] = [0.231, 0.227, 0.608];
const BACKGROUND = "hsla(235, 44%, 11%, 1)";

// Pre-blended solid colors (original color * alpha + background * (1 - alpha))
// Background ≈ hsla(235,44%,11%) → [0.062, 0.070, 0.158]
const COLOR_NODE_FADED: [number, number, number] = [0.193, 0.200, 0.275];   // COLOR_NORMAL @ 0.15
const COLOR_NODE_HOP2: [number, number, number] = [0.198, 0.200, 0.428];    // COLOR_RELATED @ 0.5
const COLOR_STROKE_FADED: [number, number, number] = [0.087, 0.094, 0.226]; // COLOR_STROKE @ 0.15
const COLOR_STROKE_HOP2: [number, number, number] = [0.147, 0.149, 0.383];  // COLOR_STROKE @ 0.5
const COLOR_LINK_DEFAULT: [number, number, number] = [0.135, 0.138, 0.369]; // COLOR_LINK @ 0.25
const COLOR_LINK_HOP2: [number, number, number] = [0.164, 0.165, 0.453];    // COLOR_LINK @ 0.35
const COLOR_LINK_FADED: [number, number, number] = [0.071, 0.078, 0.183];   // COLOR_LINK @ 0.03

const STROKE_RATIO = 0.12; // stroke thickness as fraction of radius
const SIM_ALPHA_DECAY = 0.03;
const SIM_VELOCITY_DECAY = 0.3;
const SIM_ALPHA_MIN = 0.01;

type RawNode = {
  id: string;
  artist_id_strm?: string;
  artist_name: string;
  career_stage: number;
  genres_spotify?: string[];
  genres_strm?: string[];
};

type SimNode = RawNode &
  SimulationNodeDatum & { fx?: number | null; fy?: number | null };

type RawLink = { source: string | RawNode; target: string | RawNode };

type GraphData = { nodes: RawNode[]; links: RawLink[] };

type Props = {
  data?: GraphData;
  loading: boolean;
  highlightArtist?: string;
  selectedGenre?: string;
  onArtistClick?: (artistName: string) => void;
  onWebGLError?: () => void;
  onSettlingChange?: (settling: boolean) => void;
  controls?: ReactNode;
  topRightControls?: ReactNode;
};

type HoverState = {
  node: RawNode;
  clientX: number;
  clientY: number;
};

type LabelEntry = {
  node: SimNode;
  screenX: number;
  screenY: number;
};

function nodeRadius(n: RawNode) {
  return Math.max(2, n.career_stage || 3);
}

function fillColor(
  n: RawNode,
  focusId: string | undefined,
  hop1Ids: Set<string>,
  hop2Ids: Set<string>,
  selectedGenre: string | undefined
): [number, number, number] {
  if (focusId) {
    if (n.id === focusId) return COLOR_HIGHLIGHT;
    if (hop1Ids.has(n.id)) return COLOR_RELATED;
    if (hop2Ids.has(n.id)) return COLOR_NODE_HOP2;
    return COLOR_NODE_FADED;
  }
  if (selectedGenre && n.genres_spotify?.includes(selectedGenre))
    return COLOR_GENRE;
  return COLOR_NORMAL;
}

function strokeColor(
  id: string,
  focusId: string | undefined,
  hop1Ids: Set<string>,
  hop2Ids: Set<string>
): [number, number, number] {
  if (!focusId || id === focusId || hop1Ids.has(id)) return COLOR_STROKE;
  if (hop2Ids.has(id)) return COLOR_STROKE_HOP2;
  return COLOR_STROKE_FADED;
}

const NODE_VERT = `
  attribute vec2 iPos;
  attribute float iRadius;
  attribute vec4 iFill;
  attribute vec4 iStroke;
  varying vec2 vUv;
  varying vec4 vFill;
  varying vec4 vStroke;
  void main() {
    vUv = position.xy;
    vFill = iFill;
    vStroke = iStroke;
    vec3 world = vec3(position.xy * iRadius + iPos, 0.0);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
  }
`;

const NODE_FRAG = `
  precision mediump float;
  varying vec2 vUv;
  varying vec4 vFill;
  varying vec4 vStroke;
  uniform float uStrokeRatio;
  void main() {
    float d = length(vUv);
    float fw = fwidth(d);
    float outerAlpha = 1.0 - smoothstep(1.0 - fw, 1.0, d);
    if (outerAlpha <= 0.0) discard;
    float strokeEdge = 1.0 - uStrokeRatio;
    float t = smoothstep(strokeEdge - fw, strokeEdge + fw, d);
    vec4 color = mix(vFill, vStroke, t);
    color.a *= outerAlpha * vFill.a;
    gl_FragColor = color;
  }
`;

const EDGE_VERT = `
  attribute vec4 color;
  varying vec4 vColor;
  void main() {
    vColor = color;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const EDGE_FRAG = `
  precision mediump float;
  varying vec4 vColor;
  void main() {
    gl_FragColor = vColor;
  }
`;

type EngineCallbacks = {
  onHover: (state: HoverState | null) => void;
  onNodeClick: (node: RawNode) => void;
  onBackgroundClick: () => void;
  onLabelsUpdate: (labels: LabelEntry[]) => void;
  onSettlingChange: (settling: boolean) => void;
  onProgress: (pct: number) => void;
};

const PRETICK_MAX = 300;
const PRETICK_CHUNK_MS = 8;

class GraphEngine {
  private container: HTMLDivElement;
  private callbacks: EngineCallbacks;

  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private resizeObserver: ResizeObserver;
  private width = 0;
  private height = 0;

  private nodeGeom: THREE.InstancedBufferGeometry | null = null;
  private nodeMesh: THREE.Mesh | null = null;
  private nodeMaterial: THREE.ShaderMaterial;
  private iPosAttr: THREE.InstancedBufferAttribute | null = null;
  private iRadiusAttr: THREE.InstancedBufferAttribute | null = null;
  private iFillAttr: THREE.InstancedBufferAttribute | null = null;
  private iStrokeAttr: THREE.InstancedBufferAttribute | null = null;

  private edgeGeom: THREE.BufferGeometry | null = null;
  private edgeMesh: THREE.LineSegments | null = null;
  private edgeMaterial: THREE.ShaderMaterial;
  private edgePosAttr: THREE.BufferAttribute | null = null;
  private edgeColorAttr: THREE.BufferAttribute | null = null;

  private nodes: SimNode[] = [];
  private nodesById = new Map<string, number>();
  private edges: Array<[number, number]> = [];
  private simulation: Simulation<SimNode, undefined> | null = null;

  private focusId: string | undefined;
  private hop1Ids = new Set<string>();
  private hop2Ids = new Set<string>();
  private selectedGenre: string | undefined;
  private highlightName: string | undefined;

  private zoomBehavior = d3Zoom<HTMLCanvasElement, unknown>();
  private transform: ZoomTransform = zoomIdentity;

  private rafId: number | null = null;
  private dirty = true;
  private simRunning = false;
  private settling = false;
  private settleGen = 0;

  private dragIdx: number | null = null;
  private pointerDownAt: { x: number; y: number; idx: number | null } | null =
    null;
  private hoverIdx: number | null = null;

  private labelCache: LabelEntry[] = [];
  private frameCounter = 0;

  private savedTransform: ZoomTransform | null = null;

  private disposed = false;

  constructor(container: HTMLDivElement, callbacks: EngineCallbacks) {
    this.container = container;
    this.callbacks = callbacks;

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
      failIfMajorPerformanceCaveat: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.domElement.style.display = "block";
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";
    this.renderer.domElement.style.cursor = "grab";
    this.container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1000, 1000);
    this.camera.position.set(0, 0, 10);
    this.camera.lookAt(0, 0, 0);

    this.nodeMaterial = new THREE.ShaderMaterial({
      vertexShader: NODE_VERT,
      fragmentShader: NODE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: { uStrokeRatio: { value: STROKE_RATIO } },
    });

    this.edgeMaterial = new THREE.ShaderMaterial({
      vertexShader: EDGE_VERT,
      fragmentShader: EDGE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.container);
    this.handleResize();
    this.installZoom();
    this.setInitialZoom(20);
    this.installPointerHandlers();

    this.renderLoop();
  }

  dispose() {
    this.disposed = true;
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.resizeObserver.disconnect();
    this.simulation?.stop();
    this.nodeGeom?.dispose();
    this.edgeGeom?.dispose();
    this.nodeMaterial.dispose();
    this.edgeMaterial.dispose();
    this.renderer.dispose();
    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
  }

  setData(data: GraphData | undefined) {
    if (!data) return;

    const prevPositions = new Map<string, { x: number; y: number }>();
    this.nodes.forEach((n) => {
      if (typeof n.x === "number" && typeof n.y === "number")
        prevPositions.set(n.id, { x: n.x, y: n.y });
    });

    this.nodes = data.nodes.map((raw) => {
      const prev = prevPositions.get(raw.id);
      return {
        ...raw,
        x: prev?.x ?? (Math.random() - 0.5) * 400,
        y: prev?.y ?? (Math.random() - 0.5) * 400,
      };
    });
    this.nodesById = new Map();
    this.nodes.forEach((n, i) => this.nodesById.set(n.id, i));

    this.edges = [];
    data.links.forEach((l) => {
      const sId = typeof l.source === "string" ? l.source : l.source.id;
      const tId = typeof l.target === "string" ? l.target : l.target.id;
      const sIdx = this.nodesById.get(sId);
      const tIdx = this.nodesById.get(tId);
      if (sIdx != null && tIdx != null) this.edges.push([sIdx, tIdx]);
    });

    this.rebuildNodeMesh();
    this.rebuildEdgeMesh();
    this.recomputeHops();
    this.startSimulation();
    this.refreshColors();
    this.markDirty();
  }

  setFocus(highlightArtist: string | undefined, selectedGenre: string | undefined) {
    this.highlightName = highlightArtist;
    this.selectedGenre = selectedGenre;
    const target = highlightArtist?.toLowerCase();
    const focusNode = target
      ? this.nodes.find((n) => n.artist_name?.toLowerCase() === target)
      : undefined;
    const newFocusId = focusNode?.id;

    if (newFocusId && this.focusId !== newFocusId) {
      this.savedTransform = this.transform;
    }
    if (!newFocusId && this.focusId) {
      if (this.savedTransform) {
        const saved = this.savedTransform;
        this.applyTransformAnimated(saved, 400);
        this.savedTransform = null;
      }
    }

    this.focusId = newFocusId;
    this.recomputeHops();
    if (!newFocusId) {
      this.unpinAll();
    }

    this.refreshColors();
    this.markDirty();
  }

  fitToData(padding = 40, animate = true, maxScale = Infinity, minScale = 0) {
    if (!this.nodes.length || !this.width || !this.height) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of this.nodes) {
      const x = n.x ?? 0;
      const y = n.y ?? 0;
      const r = nodeRadius(n);
      if (x - r < minX) minX = x - r;
      if (y - r < minY) minY = y - r;
      if (x + r > maxX) maxX = x + r;
      if (y + r > maxY) maxY = y + r;
    }
    if (!isFinite(minX)) return;
    const w = maxX - minX;
    const h = maxY - minY;
    const availW = this.width - padding * 2;
    const availH = this.height - padding * 2;
    const k = Math.max(Math.min(availW / w, availH / h, maxScale), minScale);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const tx = this.width / 2 - cx * k;
    const ty = this.height / 2 - cy * k;
    const t = zoomIdentity.translate(tx, ty).scale(k);
    if (animate) {
      this.applyTransformAnimated(t, 400);
    } else {
      d3Select(this.renderer.domElement).call(
        this.zoomBehavior.transform as any,
        t
      );
    }
  }

  private applyTransformAnimated(target: ZoomTransform, duration: number) {
    const canvas = this.renderer.domElement;
    d3Select(canvas)
      .transition()
      .duration(duration)
      .call(this.zoomBehavior.transform as any, target);
  }

  private markDirty() {
    this.dirty = true;
  }

  private handleResize() {
    const rect = this.container.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    if (w === this.width && h === this.height) return;
    this.width = w;
    this.height = h;
    this.renderer.setSize(w, h, false);
    this.updateCamera();
    this.markDirty();
  }

  private updateCamera() {
    const k = this.transform.k || 1;
    const w = this.width;
    const h = this.height;
    this.camera.left = -w / 2;
    this.camera.right = w / 2;
    this.camera.top = h / 2;
    this.camera.bottom = -h / 2;
    this.camera.zoom = k;
    this.camera.position.x = (w / 2 - this.transform.x) / k;
    this.camera.position.y = (this.transform.y - h / 2) / k;
    this.camera.position.z = 10;
    this.camera.updateProjectionMatrix();
  }

  private setInitialZoom(scale: number) {
    const canvas = this.renderer.domElement;
    const initialTransform = zoomIdentity
      .translate(this.width / 2, this.height / 2)
      .scale(scale);
    d3Select(canvas).call(this.zoomBehavior.transform as any, initialTransform);
  }

  private installZoom() {
    const canvas = this.renderer.domElement;
    this.zoomBehavior
      .scaleExtent([0.05, 20])
      .filter((event: any) => {
        if (this.dragIdx != null) return false;
        return !event.ctrlKey && !event.button;
      })
      .on("zoom", (event) => {
        this.transform = event.transform;
        this.updateCamera();
        this.markDirty();
      });
    d3Select(canvas).call(this.zoomBehavior as any);
  }

  private installPointerHandlers() {
    const canvas = this.renderer.domElement;
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointerleave", this.onPointerLeave);
  }

  private screenToWorld(x: number, y: number): [number, number] {
    const k = this.transform.k || 1;
    return [(x - this.transform.x) / k, (y - this.transform.y) / k];
  }

  private pickNode(clientX: number, clientY: number): number | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    const [wx, wy] = this.screenToWorld(sx, sy);
    let best: number | null = null;
    let bestDist = Infinity;
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      const dx = (n.x ?? 0) - wx;
      const dy = (n.y ?? 0) - wy;
      const r = nodeRadius(n);
      const d2 = dx * dx + dy * dy;
      if (d2 <= r * r && d2 < bestDist) {
        bestDist = d2;
        best = i;
      }
    }
    return best;
  }

  private onPointerMove = (e: PointerEvent) => {
    if (this.dragIdx != null) {
      const rect = this.renderer.domElement.getBoundingClientRect();
      const [wx, wy] = this.screenToWorld(
        e.clientX - rect.left,
        e.clientY - rect.top
      );
      const n = this.nodes[this.dragIdx];
      n.fx = wx;
      n.fy = wy;
      if (this.simulation) {
        this.simulation.alpha(Math.max(this.simulation.alpha(), 0.3)).alphaTarget(0.3);
      }
      this.simRunning = true;
      this.markDirty();
      return;
    }
    const idx = this.pickNode(e.clientX, e.clientY);
    if (idx !== this.hoverIdx) {
      this.hoverIdx = idx;
      this.renderer.domElement.style.cursor = idx != null ? "pointer" : "grab";
      this.callbacks.onHover(
        idx != null
          ? {
              node: this.nodes[idx],
              clientX: e.clientX,
              clientY: e.clientY,
            }
          : null
      );
    } else if (idx != null) {
      this.callbacks.onHover({
        node: this.nodes[idx],
        clientX: e.clientX,
        clientY: e.clientY,
      });
    }
  };

  private onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const idx = this.pickNode(e.clientX, e.clientY);
    this.pointerDownAt = { x: e.clientX, y: e.clientY, idx };
    if (idx != null) {
      this.dragIdx = idx;
      this.renderer.domElement.setPointerCapture(e.pointerId);
      this.renderer.domElement.style.cursor = "grabbing";
      e.preventDefault();
      e.stopPropagation();
    }
  };

  private onPointerUp = (e: PointerEvent) => {
    const wasDragging = this.dragIdx != null;
    if (wasDragging) {
      const n = this.nodes[this.dragIdx!];
      n.fx = n.x;
      n.fy = n.y;
      this.simulation?.alphaTarget(0);
      this.dragIdx = null;
      try {
        this.renderer.domElement.releasePointerCapture(e.pointerId);
      } catch {}
      this.renderer.domElement.style.cursor = "grab";
    }

    if (this.pointerDownAt) {
      const dx = e.clientX - this.pointerDownAt.x;
      const dy = e.clientY - this.pointerDownAt.y;
      const moved = Math.hypot(dx, dy);
      if (moved < 4) {
        if (this.pointerDownAt.idx != null) {
          this.callbacks.onNodeClick(this.nodes[this.pointerDownAt.idx]);
        } else {
          this.callbacks.onBackgroundClick();
        }
      }
    }
    this.pointerDownAt = null;
  };

  private onPointerLeave = () => {
    if (this.hoverIdx != null) {
      this.hoverIdx = null;
      this.callbacks.onHover(null);
    }
  };

  private unpinAll() {
    for (const n of this.nodes) {
      if (n.fx != null || n.fy != null) {
        n.fx = null;
        n.fy = null;
      }
    }
    if (this.simulation) {
      this.simulation.alpha(0.3).alphaTarget(0);
      this.simRunning = true;
      this.markDirty();
    }
  }

  private startSimulation() {
    this.simulation?.stop();
    const linkForce = forceLink<SimNode, { source: number; target: number }>(
      this.edges.map(([source, target]) => ({ source, target }))
    )
      .id((_, i) => i as any)
      .distance(30)
      .strength(0.4);

    this.simulation = forceSimulation<SimNode>(this.nodes)
      .force("link", linkForce as any)
      .force("charge", forceManyBody().strength(-40))
      .force("center", forceCenter(0, 0))
      .force(
        "collide",
        forceCollide<SimNode>().radius((n) => nodeRadius(n) + 1).iterations(1)
      )
      .alphaDecay(SIM_ALPHA_DECAY)
      .velocityDecay(SIM_VELOCITY_DECAY)
      .alphaMin(SIM_ALPHA_MIN)
      .stop();
    this.simRunning = false;
    this.preTick();
  }

  private preTick() {
    const sim = this.simulation;
    if (!sim) return;
    const gen = ++this.settleGen;
    this.settling = true;
    this.callbacks.onSettlingChange(true);

    const logAlphaMin = Math.log(SIM_ALPHA_MIN);
    let done = 0;
    const runChunk = () => {
      if (this.disposed || gen !== this.settleGen) return;
      const start = performance.now();
      while (done < PRETICK_MAX && performance.now() - start < PRETICK_CHUNK_MS) {
        sim.tick();
        done++;
        if ((sim.alpha() ?? 0) < SIM_ALPHA_MIN) break;
      }
      const alpha = sim.alpha() ?? SIM_ALPHA_MIN;
      const settled = alpha < SIM_ALPHA_MIN || done >= PRETICK_MAX;
      const pct = settled
        ? 1
        : Math.min(1, Math.max(0, Math.log(alpha) / logAlphaMin));
      this.callbacks.onProgress(pct);
      if (!settled) {
        requestAnimationFrame(runChunk);
        return;
      }
      this.writeNodePositions();
      this.writeEdgePositions();
      this.fitToData(40, false, 20, 0.2);
      this.renderer.render(this.scene, this.camera);
      this.dirty = false;
      this.computeLabels();
      this.settling = false;
      this.callbacks.onSettlingChange(false);
    };
    requestAnimationFrame(runChunk);
  }

  private rebuildNodeMesh() {
    const N = this.nodes.length;
    this.nodeGeom?.dispose();

    const tri = new THREE.PlaneGeometry(2, 2, 1, 1);
    const geom = new THREE.InstancedBufferGeometry();
    geom.index = tri.index;
    geom.attributes.position = tri.attributes.position;
    geom.instanceCount = N;

    const iPos = new Float32Array(N * 2);
    const iRadius = new Float32Array(N);
    const iFill = new Float32Array(N * 4);
    const iStroke = new Float32Array(N * 4);

    this.iPosAttr = new THREE.InstancedBufferAttribute(iPos, 2);
    this.iRadiusAttr = new THREE.InstancedBufferAttribute(iRadius, 1);
    this.iFillAttr = new THREE.InstancedBufferAttribute(iFill, 4);
    this.iStrokeAttr = new THREE.InstancedBufferAttribute(iStroke, 4);
    this.iPosAttr.setUsage(THREE.DynamicDrawUsage);
    this.iFillAttr.setUsage(THREE.DynamicDrawUsage);
    this.iStrokeAttr.setUsage(THREE.DynamicDrawUsage);

    geom.setAttribute("iPos", this.iPosAttr);
    geom.setAttribute("iRadius", this.iRadiusAttr);
    geom.setAttribute("iFill", this.iFillAttr);
    geom.setAttribute("iStroke", this.iStrokeAttr);

    for (let i = 0; i < N; i++) {
      iRadius[i] = nodeRadius(this.nodes[i]);
    }
    this.iRadiusAttr.needsUpdate = true;

    this.nodeGeom = geom;

    if (this.nodeMesh) this.scene.remove(this.nodeMesh);
    const mesh = new THREE.Mesh(geom, this.nodeMaterial);
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    this.nodeMesh = mesh;
    this.scene.add(mesh);
  }

  private rebuildEdgeMesh() {
    const E = this.edges.length;
    this.edgeGeom?.dispose();

    const geom = new THREE.BufferGeometry();
    const pos = new Float32Array(E * 2 * 3);
    const col = new Float32Array(E * 2 * 4);
    this.edgePosAttr = new THREE.BufferAttribute(pos, 3);
    this.edgeColorAttr = new THREE.BufferAttribute(col, 4);
    this.edgePosAttr.setUsage(THREE.DynamicDrawUsage);
    this.edgeColorAttr.setUsage(THREE.DynamicDrawUsage);
    geom.setAttribute("position", this.edgePosAttr);
    geom.setAttribute("color", this.edgeColorAttr);
    this.edgeGeom = geom;

    if (this.edgeMesh) this.scene.remove(this.edgeMesh);
    const mesh = new THREE.LineSegments(geom, this.edgeMaterial);
    mesh.frustumCulled = false;
    mesh.renderOrder = 1;
    this.edgeMesh = mesh;
    this.scene.add(mesh);
  }

  private writeNodePositions() {
    if (!this.iPosAttr) return;
    const arr = this.iPosAttr.array as Float32Array;
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      arr[i * 2] = n.x ?? 0;
      arr[i * 2 + 1] = -(n.y ?? 0);
    }
    this.iPosAttr.needsUpdate = true;
  }

  private writeEdgePositions() {
    if (!this.edgePosAttr) return;
    const arr = this.edgePosAttr.array as Float32Array;
    for (let i = 0; i < this.edges.length; i++) {
      const [s, t] = this.edges[i];
      const a = this.nodes[s];
      const b = this.nodes[t];
      arr[i * 6 + 0] = a.x ?? 0;
      arr[i * 6 + 1] = -(a.y ?? 0);
      arr[i * 6 + 2] = 0;
      arr[i * 6 + 3] = b.x ?? 0;
      arr[i * 6 + 4] = -(b.y ?? 0);
      arr[i * 6 + 5] = 0;
    }
    this.edgePosAttr.needsUpdate = true;
  }

  private recomputeHops() {
    this.hop1Ids = new Set();
    this.hop2Ids = new Set();
    if (!this.focusId) return;
    const focusId = this.focusId;
    this.edges.forEach(([s, t]) => {
      const sId = this.nodes[s].id;
      const tId = this.nodes[t].id;
      if (sId === focusId) this.hop1Ids.add(tId);
      if (tId === focusId) this.hop1Ids.add(sId);
    });
    this.edges.forEach(([s, t]) => {
      const sId = this.nodes[s].id;
      const tId = this.nodes[t].id;
      if (this.hop1Ids.has(sId) && tId !== focusId && !this.hop1Ids.has(tId))
        this.hop2Ids.add(tId);
      if (this.hop1Ids.has(tId) && sId !== focusId && !this.hop1Ids.has(sId))
        this.hop2Ids.add(sId);
    });
  }

  private refreshColors() {
    if (this.iFillAttr && this.iStrokeAttr) {
      const fill = this.iFillAttr.array as Float32Array;
      const stroke = this.iStrokeAttr.array as Float32Array;
      for (let i = 0; i < this.nodes.length; i++) {
        const n = this.nodes[i];
        const [r, g, b] = fillColor(
          n,
          this.focusId,
          this.hop1Ids,
          this.hop2Ids,
          this.selectedGenre
        );
        const [sr, sg, sb] = strokeColor(n.id, this.focusId, this.hop1Ids, this.hop2Ids);
        fill[i * 4] = r;
        fill[i * 4 + 1] = g;
        fill[i * 4 + 2] = b;
        fill[i * 4 + 3] = 1;
        stroke[i * 4] = sr;
        stroke[i * 4 + 1] = sg;
        stroke[i * 4 + 2] = sb;
        stroke[i * 4 + 3] = 1;
      }
      this.iFillAttr.needsUpdate = true;
      this.iStrokeAttr.needsUpdate = true;
    }
    if (this.edgeColorAttr) {
      const arr = this.edgeColorAttr.array as Float32Array;
      for (let i = 0; i < this.edges.length; i++) {
        const [s, t] = this.edges[i];
        const sId = this.nodes[s].id;
        const tId = this.nodes[t].id;
        let c: [number, number, number];
        if (!this.focusId) {
          c = COLOR_LINK_DEFAULT;
        } else if (sId === this.focusId || tId === this.focusId) {
          c = COLOR_LINK_HIGHLIGHT;
        } else if (
          (this.hop1Ids.has(sId) && this.hop2Ids.has(tId)) ||
          (this.hop1Ids.has(tId) && this.hop2Ids.has(sId))
        ) {
          c = COLOR_LINK_HOP2;
        } else {
          c = COLOR_LINK_FADED;
        }
        for (let v = 0; v < 2; v++) {
          const off = (i * 2 + v) * 4;
          arr[off] = c[0];
          arr[off + 1] = c[1];
          arr[off + 2] = c[2];
          arr[off + 3] = 1;
        }
      }
      this.edgeColorAttr.needsUpdate = true;
    }
  }

  private computeLabels() {
    const k = this.transform.k;
    const out: LabelEntry[] = [];
    const showAll = k > 3;
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      const isFocus = this.focusId === n.id;
      const isHighStage = (n.career_stage ?? 0) >= 8 && k > 1;
      if (!(isFocus || isHighStage || showAll)) continue;
      const sx = (n.x ?? 0) * k + this.transform.x;
      const sy = (n.y ?? 0) * k + this.transform.y;
      if (sx < -80 || sx > this.width + 80 || sy < -40 || sy > this.height + 40)
        continue;
      out.push({ node: n, screenX: sx, screenY: sy });
      if (out.length > 200) break;
    }
    this.labelCache = out;
    this.callbacks.onLabelsUpdate(out);
  }

  private renderLoop = () => {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this.renderLoop);

    if (this.settling) return;

    if (this.simulation && this.simRunning) {
      this.simulation.tick();
      if ((this.simulation.alpha() ?? 0) < SIM_ALPHA_MIN && this.dragIdx == null) {
        this.simRunning = false;
      }
      this.writeNodePositions();
      this.writeEdgePositions();
      this.dirty = true;
    }

    if (!this.dirty) return;
    this.renderer.render(this.scene, this.camera);
    this.dirty = false;

    if (this.simRunning) {
      this.frameCounter = (this.frameCounter + 1) % 3;
      if (this.frameCounter !== 0) return;
    }
    this.computeLabels();
  };
}

const ArtistGraphThree: React.FC<Props> = ({
  data,
  loading,
  highlightArtist,
  selectedGenre,
  onArtistClick,
  onWebGLError,
  onSettlingChange,
  controls,
  topRightControls,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<GraphEngine | null>(null);
  const onArtistClickRef = useRef(onArtistClick);
  const onWebGLErrorRef = useRef(onWebGLError);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [labels, setLabels] = useState<LabelEntry[]>([]);
  const [settling, setSettling] = useState(false);
  const [showSettlingOverlay, setShowSettlingOverlay] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    onArtistClickRef.current = onArtistClick;
  }, [onArtistClick]);

  useEffect(() => {
    onWebGLErrorRef.current = onWebGLError;
  }, [onWebGLError]);

  useEffect(() => {
    onSettlingChange?.(settling);
  }, [settling, onSettlingChange]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let engine: GraphEngine;
    try {
      engine = new GraphEngine(el, {
        onHover: setHover,
        onNodeClick: (n) => onArtistClickRef.current?.(n.artist_name),
        onBackgroundClick: () => {},
        onLabelsUpdate: setLabels,
        onSettlingChange: setSettling,
        onProgress: setProgress,
      });
    } catch (err) {
      onWebGLErrorRef.current?.();
      return;
    }
    engineRef.current = engine;
    return () => {
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    if (!data) return;
    setSettling(true);
    engineRef.current?.setData(data);
  }, [data]);

  useEffect(() => {
    engineRef.current?.setFocus(highlightArtist, selectedGenre);
  }, [highlightArtist, selectedGenre]);

  useEffect(() => {
    if (loading) {
      setShowSettlingOverlay(true);
      return;
    }
    if (!settling) {
      const timer = window.setTimeout(() => setShowSettlingOverlay(false), 300);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => setShowSettlingOverlay(true), 250);
    return () => window.clearTimeout(timer);
  }, [loading, settling]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: BACKGROUND,
        overflow: "hidden",
      }}
    >
      {controls && !loading && !showSettlingOverlay && (
        <div className="controls-panel-stack">{controls}</div>
      )}
      {topRightControls && !loading && !showSettlingOverlay && (
        <div className="controls-panel-stack controls-panel-stack--right">
          {topRightControls}
        </div>
      )}
      <Labels labels={labels} focusName={highlightArtist} />
      {hover && <Tooltip state={hover} />}
      {(loading || showSettlingOverlay) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
            background: BACKGROUND,
            zIndex: 20,
          }}
        >
          <Spin spinning />
          {showSettlingOverlay && !loading && (
            <div style={{ width: 160, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.12)" }}>
              <div
                style={{
                  height: "100%",
                  borderRadius: 2,
                  background: "#f680c8",
                  width: `${Math.round(progress * 100)}%`,
                  transition: "width 80ms linear",
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Labels: React.FC<{ labels: LabelEntry[]; focusName?: string }> = ({
  labels,
  focusName,
}) => {
  const target = focusName?.toLowerCase();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        fontFamily: "cabin, sans-serif",
        fontSize: 11,
        color: "#fff",
        textShadow: "0 1px 2px rgba(0,0,0,0.9)",
      }}
    >
      {labels.map(({ node, screenX, screenY }) => {
        const isFocus =
          target && node.artist_name?.toLowerCase() === target;
        return (
          <div
            key={node.id}
            style={{
              position: "absolute",
              left: screenX,
              top: screenY + nodeRadius(node) + 2,
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              fontWeight: isFocus ? 700 : 400,
            }}
          >
            {node.artist_name}
          </div>
        );
      })}
    </div>
  );
};

export default ArtistGraphThree;
